<template>
  <SectionFrame v-if="shown || inStudio" :section="section" :index="index" :heading="layout === 'line' ? '' : heading" label="Opening hours" :narrow="layout !== 'week'">
    <template v-if="shown">
      <!-- One line: for the top of a page or a footer-ish band. -->
      <p v-if="layout === 'line'" class="s2-hours-line t-body mo-reveal">
        <span v-if="state && section.showOpenNow !== false" class="s2-open" :data-open="state.open">{{ stateText }}</span>
        <span>{{ lineText }}</span>
      </p>
      <div v-else :class="layout === 'week' ? 's2-hours-week' : 's2-hours-today'">
        <div class="mo-reveal">
          <p v-if="state && section.showOpenNow !== false" class="s2-open" :data-open="state.open">{{ stateText }}</p>
          <table class="s2-hours-table t-body">
            <caption class="s2-hp">Opening hours</caption>
            <tbody>
            <tr v-for="row in rows" :key="row.days" :aria-current="row.includes(today) ? 'date' : null">
              <th scope="row">{{ row.days }}</th>
              <td>{{ row.text }}</td>
            </tr>
            </tbody>
          </table>
          <p v-if="hours.note" class="s2-note">{{ hours.note }}</p>
        </div>
        <div v-if="section.showClosures !== false && closures.length" class="t-card s2-closures mo-reveal">
          <h3 class="t-h3">Holidays and changes</h3>
          <ul>
            <li v-for="c in closures" :key="c.from + c.to">
              <span><b v-if="c.label">{{ c.label }}</b><span class="s2-muted">{{ c.when }}</span></span>
              <span :class="c.what === 'Closed' ? 's2-closed' : ''">{{ c.what }}</span>
            </li>
          </ul>
        </div>
      </div>
    </template>
    <StudioGap v-else>Add your opening hours (Business settings), then they show here.</StudioGap>
  </SectionFrame>
</template>

<script setup>
import {computed} from "vue";
import SectionFrame from "./SectionFrame.vue";
import StudioGap from "./StudioGap.vue";
import {closureText, hasHours, layoutOf, localParts, openNow, openNowText, upcomingClosures, weekRows} from "@/utils/sectionLogic.js";
import {studioMode} from "@/classes/previewBridge.js";

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}, data: {type: Object, default: () => ({})}});
const inStudio = computed(() => studioMode.value !== "off");
const layout = computed(() => layoutOf(props.section, ["week", "today", "line"]));
const heading = computed(() => props.section.heading ?? "When we're open");
const hours = computed(() => props.data.hours || {});
const shown = computed(() => hasHours(props.data.hours));
// Server render and client agree within the cached minute or two; "Open now"
// is worked out where the page is drawn, in the business's time (Europe/London).
const now = new Date();
const state = computed(() => openNow(hours.value, now));
const stateText = computed(() => openNowText(state.value));
const today = computed(() => localParts(now).day);
const rows = computed(() => weekRows(hours.value));
const closures = computed(() => upcomingClosures(hours.value, now).slice(0, 4).map(closureText).map((c, i) => ({...c, from: String(i), to: c.when})));
const lineText = computed(() => rows.value.map((r) => `${r.days} ${r.text}`).join(" · "));
</script>

<style>
.s2-hours-week { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); gap: var(--t-gap); align-items: start; }
@media (max-width: 760px) { .s2-hours-week { grid-template-columns: minmax(0, 1fr); } }
.s2-hours-today .s2-closures { margin-top: 1.25rem; }
.s2-hours-table { width: 100%; border-collapse: collapse; margin-top: .75rem; }
.s2-hours-table th, .s2-hours-table td { padding: .55rem 0; border-bottom: 1px solid var(--t-line); text-align: left; font-weight: 400; }
.s2-hours-table td { text-align: right; }
.s2-hours-table tr[aria-current="date"] th, .s2-hours-table tr[aria-current="date"] td { font-weight: 700; }
.s2-open { display: inline-flex; align-items: center; gap: .45rem; padding: .35rem .75rem; border-radius: 999px; font-weight: 700; font-size: .9rem;
  background: color-mix(in srgb, #b42318 10%, var(--t-surface)); color: #8f1d14; }
.s2-open::before { content: ""; width: .5rem; height: .5rem; border-radius: 50%; background: currentColor; }
.s2-open[data-open="true"] { background: color-mix(in srgb, #1d9a4a 14%, var(--t-surface)); color: #14652f; }
html[data-theme-dark="true"] .s2-open { color: #ffb4ab; } html[data-theme-dark="true"] .s2-open[data-open="true"] { color: #8be0a4; }
.s2-closures ul { list-style: none; margin: .5rem 0 0; padding: 0; }
.s2-closures li { display: flex; justify-content: space-between; gap: 1rem; padding: .5rem 0; border-bottom: 1px solid var(--t-line); }
.s2-closures li > span:first-child { display: flex; flex-direction: column; }
.s2-closed { font-weight: 700; color: var(--t-accent); }
.s2-hours-line { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem 1rem; margin: 0; justify-content: center; text-align: center; }
</style>
